import { useState, useMemo, useCallback, useEffect } from "react";
import { Button, InputField, DropDownInput, DataTable } from "@/components";
import ColumnHelper from "@/utils/ColumnHelper";
import { useFilteredData, useAlert, useConfirmationDialog } from "@/";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCallRequest,
  removeCallBack,
  selectAllCallbacks,
} from "../../../app/Slices";
import { RightWrapper } from "../../../components";
import { RiFileExcel2Line } from "react-icons/ri";

const CallBackRequest = () => {
  const { alert, AlertDialog } = useAlert();
  const { confirm, ConfirmationModal } = useConfirmationDialog();
  const statusType = useMemo(
    () => [
      {
        label: "Pickup",
        value: "pickupDelivery",
      },
      {
        label: "Pre Delivery",
        value: "shipmentNdrRto",
      },
      {
        label: "Claim",
        value: "weightDispute",
      },
      {
        label: "Finance",
        value: "finance",
      },
      {
        label: "KYC",
        value: "kycBankVerification",
      },
      {
        label: "Technical Support",
        value: "technicalSupport",
      },
      {
        label: "Billing & Taxation",
        value: "billingTaxation",
      },
      {
        label: "Post Delivery",
        value: "claims",
      },
      {
        label: "Other",
        value: "other",
      },
    ],
    [],
  );
  const dispatch = useDispatch();

  const [filters, setFilters] = useState({
    customerName: "",
    customerId: "",
    category: "",
  });

  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [resetPageCounter, setResetPageCounter] = useState(0);

  const fetchData = async () => {
    try {
      const response = await dispatch(fetchCallRequest());

      if (fetchCallRequest.fulfilled.match(response)) {
        console.log(
          "Call request data fetched successfully:",
          response.payload,
        );
      } else if (fetchCallRequest.rejected.match(response)) {
        console.error("Failed to fetch call request data:", response.payload);
      }
    } catch (error) {
      console.error("Error fetching call request data:", error);
      throw error;
    }
  };

  useEffect(() => {
    fetchData();
  }, [dispatch]);
  // Ensure rawData is only generated once

  const callback = useSelector(selectAllCallbacks) || [];

  // Filter configuration
  const filterConfig = useMemo(
    () => ({
      customerName: { type: "partial", itemKey: "userId.name" },
      customerId: { type: "partial", itemKey: "userId?.clientId" },
      category: { type: "exact", itemKey: "query" },
    }),
    [],
  );

  // Format date values
  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);
  // Memoize filtered data to prevent unnecessary re-renders
  const { data: finalFilteredData, isLoading } = useFilteredData(
    callback,
    appliedFilters,
    filterConfig,
  );
  const handleDelete = async (id) => {
    try {
      console.log("Callback Request  with ID:", id);
      await confirm("Are you sure you want to delete this Callback Request ?");
      const response = await dispatch(removeCallBack(id));

      if (removeCallBack.fulfilled.match(response)) {
        console.log(
          "Callback Request  deleted successfully:",
          response.payload,
        );
        await alert("Callback Request  deleted successfully");
      } else {
        console.error("Failed to delete Callback Request :", response.payload);
        await alert(
          `Failed to delete Callback Request : ${
            response.payload || "Unknown error"
          }`,
        );
      }
    } catch ({ error }) {
      console.error("Error deleting Callback Request :", error.message);
      await alert(
        "An unexpected error occurred while deleting the Callback Request ",
      );
    }
  };
  // Memoized table columns
  const columns = useMemo(
    () => [
      ColumnHelper({
        header: "Customer Name",
        accessorKey: "",
        cell: (info) => info.row.original.userId?.name || "N/A",
        meta: {
          tooltip: (row) => row.userId?.name || "N/A",
        },
      }),
      ColumnHelper({
        header: "Customer ID",
        accessorKey: "",
        cell: (info) => info.row.original.userId?.clientId || "N/A",
        meta: {
          tooltip: (row) => row.userId?.clientId || "N/A",
        },
      }),
      ColumnHelper({
        header: "Mobile No.",
        accessorKey: "phone",
        meta: {
          tooltip: (row) => row.phone || "N/A",
        },
      }),
      ColumnHelper({
        header: "Category",
        accessorKey: "query",
        meta: {
          tooltip: (row) => row.query || "N/A",
        },
      }),
      ColumnHelper({
        header: "Message",
        accessorKey: "message",
        meta: {
          tooltip: (row) => row.message || "N/A",
        },
      }),
      ColumnHelper({
        header: "Callback Date & Time",
        accessorKey: "callbackTime",
        cell: (info) => {
          const timeVal = info.row.original.callbackTime;
          if (!timeVal) return formatDate(info.row.original.createdAt);
          return new Date(timeVal).toLocaleString("en-IN", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
        },
        meta: {
          tooltip: (row) =>
            row.callbackTime
              ? new Date(row.callbackTime).toLocaleString("en-IN")
              : row.createdAt || "N/A",
        },
      }),
      ColumnHelper({
        header: "Created At",
        accessorKey: "createdAt",
        cell: (info) => formatDate(info.row.original.createdAt) || "N/A",
        meta: {
          tooltip: (row) => formatDate(row.createdAt) || "N/A",
        },
      }),
      ColumnHelper({
        header: "Actions",
        accessorKey: "_id",
        cell: (info) => (
          <div className="flex gap-1 justify-start items-center">
            <Button
              size="xs"
              variant="destructive"
              onClick={() => handleDelete(info.row.original._id)}
            >
              Delete
            </Button>
          </div>
        ),
        meta: {
          tooltip: () => " delete CallBack Request",
        },
      }),
    ],
    [],
  );

  // Handle filter changes
  const handleChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleDateChange = useCallback((dateRange) => {
    setFilters((prev) => ({
      ...prev,
      date: { from: dateRange?.from || "", to: dateRange?.to || "" },
    }));
  }, []);

  // Apply filters without resetting raw data
  const handleApply = useCallback(() => {
    setAppliedFilters(filters);
    setResetPageCounter((prev) => prev + 1);
  }, [filters]);

  // Reset filters properly
  const handleReset = useCallback(() => {
    const initialFilters = {
      customerName: "",
      customerId: "",
      category: "",
    };
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setResetPageCounter((prev) => prev + 1);
  }, []);
  return (
    <RightWrapper>
      <div className="flex flex-col gap-4 mt-2 px-2 relative pb-20 md:pb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          {/* Date Filter */}
          <InputField
            label="Customer Name"
            placeholder="Enter customer name"
            name="customerName"
            value={filters.customerName}
            onChange={(value) => handleChange("customerName", value)}
          />
          <InputField
            label="Customer ID"
            placeholder="Enter customer ID"
            name="customerId"
            value={filters.customerId}
            onChange={(value) => handleChange("customerId", value)}
          />

          {/* Status Dropdown */}
          <DropDownInput
            label="EscalationsStatus"
            name="status"
            placeholder="Select Status"
            options={statusType}
            value={filters.category}
            onChange={(value) => handleChange("category", value)}
          />

          {/* Action Buttons */}
          <div className="flex gap-2 w-full md:w-fit justify-end items-end">
            <Button
              variant="default"
              size="md"
              onClick={handleApply}
              className="h-12"
            >
              Apply
            </Button>
            <Button
              variant="outline"
              size="md"
              onClick={handleReset}
              className="h-12"
            >
              Reset
            </Button>
          </div>
        </div>
        <DataTable
          columns={columns}
          tittle="All Requests"
          data={finalFilteredData}
          showCheckbox={false}
          isLoading={isLoading}
          resetPage={resetPageCounter}
          actions={[
            {
              name: "Export All",
              onClick: () => {},
              icon: RiFileExcel2Line,
              body: {
                model: "Callback Requests",
                filename: `Callback_Requests_${new Date().getTime()}`,
              },
            },
          ]}
        />
      </div>
      <AlertDialog />
      <ConfirmationModal />
    </RightWrapper>
  );
};

export default CallBackRequest;
